import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { Inventory } from 'src/replicant/buruburu/inventory';
import {
  getGemsToEnergyExchangeRates,
  getGemsToPointsExchangeRates,
} from 'src/replicant/buruburu/inventory/inventory.ruleset';
import PopupMonitor from '../logic/PopupMonitor';
import GCInstant from '@play-co/gcinstant';
import { AB } from 'src/lib/AB';

/** Check if user has the required energy */
function hasEnoughEnergy(energyRequired: number) {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  return Inventory.getters(user).getEnergy(now) >= energyRequired;
}

/** Check if user has the required energy */
function hasEnoughCoins(coinsRequired: number) {
  const { user } = StateObserver.getState();
  return Inventory.getters(user).getPoints() >= coinsRequired;
}

/** Check if user has enough gems to buy the cheapest energy product */
function hasEnoughGemsToBuyEnergy() {
  const { user } = StateObserver.getState();
  const gems = Inventory.getters(user).getGems();
  const rates = getGemsToEnergyExchangeRates().sort(
    (a, b) => a.premium - b.premium,
  );
  const cheapest = rates[0].premium;
  return gems >= cheapest;
}

/** Check if user has enough gems to buy the cheapest energy product */
function hasEnoughGemsToBuyCoins() {
  const { user } = StateObserver.getState();
  const gems = Inventory.getters(user).getGems();
  const rates = getGemsToPointsExchangeRates().sort(
    (a, b) => a.premium - b.premium,
  );
  const cheapest = rates[0].premium;
  return gems >= cheapest;
}

/** Open the coins shop popup and await for the outcome if user has purchased any */
async function buyCoins(titleKey?: string) {
  await openPopupPromise('popupCoinsShop', { titleKey });

  // If popupCoinsExchanged is open, that means the user has purchased coins
  if (PopupMonitor.isOpen('popupCoinsExchanged')) {
    // Await user receive purchased coins
    await new Promise<void>((resolve) => {
      const onCloseHandler = () => {
        PopupMonitor.removeOnCloseHandler(
          'popupCoinsExchanged',
          onCloseHandler,
        );
        resolve();
      };
      PopupMonitor.addOnCloseHandler('popupCoinsExchanged', onCloseHandler);
    });
  }
}

/** Open the energy shop popup and await for the outcome if user has purchased any */
async function buyEnergy(titleKey?: string) {
  await openPopupPromise('popupEnergyShop', { titleKey });

  // If popupEnergyExchanged is open, that means the user has purchased energy
  if (PopupMonitor.isOpen('popupEnergyExchanged')) {
    // Await user receive purchased energy
    await new Promise<void>((resolve) => {
      const onCloseHandler = () => {
        PopupMonitor.removeOnCloseHandler(
          'popupEnergyExchanged',
          onCloseHandler,
        );
        resolve();
      };
      PopupMonitor.addOnCloseHandler('popupEnergyExchanged', onCloseHandler);
    });
  }
}

/** Open the gems shop popup and await for the outcome if user has purchased any */
export async function buyGems(titleKey?: string) {
  await openPopupPromise('popupGemsShop', { titleKey, closeOnPurchase: true });

  // If popupPurchaseComplete is open, that means the user has purchased gems
  if (PopupMonitor.isOpen('popupPurchaseComplete')) {
    // Await user receive purchased gems
    await new Promise<void>((resolve) => {
      const onCloseHandler = () => {
        PopupMonitor.removeOnCloseHandler(
          'popupPurchaseComplete',
          onCloseHandler,
        );
        resolve();
      };
      PopupMonitor.addOnCloseHandler('popupPurchaseComplete', onCloseHandler);
    });
  }
}

async function inviteFriends() {
  await openPopupPromise('popupShareInvite', {
    subFeature: 'invite_share_gems_reward',
  });
}

export async function runShopOutOfEnergySequence(energyRequired: number) {
  // Check if user already has enough energy
  if (hasEnoughEnergy(energyRequired)) return true;

  // Analytics
  GCInstant.trackPinch({
    resource: 'energy',
    trigger: 'outofenergy',
  });

  if (hasEnoughGemsToBuyEnergy()) {
    // User has enough gems to buy energy, open energy shop popup
    await buyEnergy('currencyExchange.energyShop.titleOutOfEnergy');
  } else {
    // User does not has enough gems to buy energy, open gems shop popup instead
    await buyGems('currencyExchange.gemsShop.titleOutOfEnergy');

    // Try invite friends if the player does not have enough gems
    if (!hasEnoughGemsToBuyEnergy()) {
      await inviteFriends();
    }

    // Open EnergyShop - user can buy more gems from there if needed
    await buyEnergy('currencyExchange.energyShop.titleOutOfEnergy');
  }

  // Return true is user got the energy required
  return hasEnoughEnergy(energyRequired);
}

export async function runShopOutOfCoinsSequence(coinsRequired: number) {
  // Check if user already has enough coins
  if (hasEnoughCoins(coinsRequired)) return true;

  // Analytics
  GCInstant.trackPinch({
    resource: 'coins',
    trigger: 'outofcoins',
  });

  if (hasEnoughGemsToBuyCoins()) {
    // User has enough gems to buy coins, open coins shop popup
    await buyCoins('currencyExchange.coinsShop.titleOutOfCoins');
  } else {
    // User does not has enough gems to buy coins, open gems shop popup instead
    await buyGems('currencyExchange.gemsShop.titleOutOfCoins');

    // Try invite friends if the player does not have enough gems
    if (!hasEnoughGemsToBuyCoins()) {
      await inviteFriends();
    }

    // Open CoinsShop - user can buy more gems from there if needed
    await buyCoins('currencyExchange.coinsShop.titleOutOfCoins');
  }

  // Return true is user got the coins required
  return hasEnoughCoins(coinsRequired);
}
